/**
 * MC Analytics -- Chart rendering, server selector, heatmap, and session DataTable.
 *
 * Initializes on /mc/analytics page only (guards on #mc-analytics-chart presence).
 * Fetches chart data via AJAX from McPresenter::handleGetAnalyticsChart signal.
 * Renders Highcharts stacked area chart (all servers) or single area chart (one server).
 * Renders GitHub-style activity heatmap via McPresenter::handleGetHeatmap signal.
 * Initializes server-side DataTable for session history via McPresenter::handleGetSessions signal.
 */

import * as Highcharts from 'highcharts';
import flatpickr from 'flatpickr';
import DataTable from 'datatables.net-bs5';
import { init_tooltips } from '../util';
import { getChartTheme } from '../utils/chartTheme';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ChartSeries {
    name: string;
    points: [number, number][];
}

interface ChartPayload {
    series: ChartSeries[];
    stacked: boolean;
}

interface HeatmapDay {
    hours: number;
    players: string;
}

interface HeatmapPayload {
    days: Record<string, HeatmapDay>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const SERVER_COLORS = [
    '#2196F3',
    '#4CAF50',
    '#FF9800',
    '#9C27B0',
    '#00BCD4',
    '#F44336',
    '#795548',
    '#607D8B',
    '#E91E63',
    '#3F51B5',
];

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

let currentServer: string = 'all';
let currentRange: string = '24h';
let chartInstance: Highcharts.Chart | null = null;
let customFrom: string | undefined;
let customTo: string | undefined;
let sessionsTable: any | null = null;

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export function initAnalytics(): void {
    const chartContainer = document.getElementById('mc-analytics-chart');
    if (!chartContainer) return; // Only runs on /mc/analytics page

    // Bind server selector
    const selector = document.getElementById('mc-server-selector') as HTMLSelectElement | null;
    if (selector) {
        selector.addEventListener('change', () => {
            currentServer = selector.value;
            customFrom = undefined;
            customTo = undefined;
            fetchChartData();
            fetchHeatmapData();
            if (sessionsTable) sessionsTable.ajax.reload();
        });
    }

    // Bind time range buttons
    document.querySelectorAll<HTMLElement>('.mc-range-btn').forEach((btn) => {
        btn.addEventListener('click', () => {
            const range = btn.dataset.range;
            if (!range) return;

            currentRange = range;
            customFrom = undefined;
            customTo = undefined;

            // Toggle active classes
            document.querySelectorAll('.mc-range-btn').forEach((b) => {
                b.classList.remove('btn-primary');
                b.classList.add('btn-outline-secondary');
            });
            btn.classList.remove('btn-outline-secondary');
            btn.classList.add('btn-primary');

            fetchChartData();
        });
    });

    // Bind custom date range via flatpickr
    const customInput = document.getElementById('mc-custom-range') as HTMLInputElement | null;
    if (customInput) {
        if (!(customInput as any)._flatpickr) { // idempotency guard (D-15, INPUT-01)
        flatpickr(customInput, {
            mode: 'range',
            dateFormat: 'Y-m-d',
            onChange: (selectedDates: Date[]) => {
                if (selectedDates.length === 2) {
                    customFrom = formatDate(selectedDates[0]);
                    customTo = formatDate(selectedDates[1]);
                    currentRange = 'custom';

                    // Clear active state from range buttons when using custom
                    document.querySelectorAll('.mc-range-btn').forEach((b) => {
                        b.classList.remove('btn-primary');
                        b.classList.add('btn-outline-secondary');
                    });

                    fetchChartData(customFrom, customTo);
                }
            },
        });
        } // end _flatpickr guard
    }

    // Init session DataTable
    initSessionsTable();

    // Initial load
    fetchChartData();
    fetchHeatmapData();
}

export function getCurrentServer(): string {
    return currentServer;
}

export function reloadSessions(): void {
    if (sessionsTable) sessionsTable.ajax.reload();
}

// ---------------------------------------------------------------------------
// DataTable initialization
// ---------------------------------------------------------------------------

function initSessionsTable(): void {
    const tableEl = document.querySelector('.datatable[data-table="mc-sessions"]');
    if (!tableEl) return;

    sessionsTable = new DataTable('.datatable[data-table="mc-sessions"]', {
        pageLength: 25,
        processing: true,
        serverSide: true,
        ajax: {
            url: '?do=getSessions',
            data: function (data: any) {
                data.server = getCurrentServer();
                data.player = getPlayerFilter();
                data.dateFrom = getDateFrom();
                data.dateTo = getDateTo();
                return data;
            },
        },
        columns: [
            { data: 'player' },
            { data: 'server' },
            { data: 'joined' },
            {
                data: 'left',
                render: function (data: any) {
                    if (data === null) {
                        return '<span class="mc-online-badge"><span class="mc-online-dot"></span> Online now</span>';
                    }
                    return data;
                },
            },
            { data: 'duration' },
        ],
        order: [[2, 'desc']],
    });

    // Bind player name filter with debounce
    const playerFilter = document.getElementById('mc-player-filter') as HTMLInputElement | null;
    if (playerFilter) {
        let debounceTimer: ReturnType<typeof setTimeout>;
        playerFilter.addEventListener('keyup', () => {
            clearTimeout(debounceTimer);
            debounceTimer = setTimeout(() => {
                if (sessionsTable) sessionsTable.ajax.reload();
            }, 300);
        });
    }

    // Bind date range filters via flatpickr
    const dateFromInput = document.getElementById('mc-sessions-date-from') as HTMLInputElement | null;
    const dateToInput = document.getElementById('mc-sessions-date-to') as HTMLInputElement | null;

    if (dateFromInput) {
        if (!(dateFromInput as any)._flatpickr) { // idempotency guard (D-15, INPUT-01)
            flatpickr(dateFromInput, {
                dateFormat: 'Y-m-d',
                onChange: () => {
                    if (sessionsTable) sessionsTable.ajax.reload();
                },
            });
        }
    }

    if (dateToInput) {
        if (!(dateToInput as any)._flatpickr) { // idempotency guard (D-15, INPUT-01)
            flatpickr(dateToInput, {
                dateFormat: 'Y-m-d',
                onChange: () => {
                    if (sessionsTable) sessionsTable.ajax.reload();
                },
            });
        }
    }
}

function getPlayerFilter(): string {
    const el = document.getElementById('mc-player-filter') as HTMLInputElement | null;
    return el ? el.value : '';
}

function getDateFrom(): string {
    const el = document.getElementById('mc-sessions-date-from') as HTMLInputElement | null;
    return el ? el.value : '';
}

function getDateTo(): string {
    const el = document.getElementById('mc-sessions-date-to') as HTMLInputElement | null;
    return el ? el.value : '';
}

// ---------------------------------------------------------------------------
// Heatmap
// ---------------------------------------------------------------------------

export async function fetchHeatmapData(): Promise<void> {
    const url = `?do=getHeatmap&server=${encodeURIComponent(currentServer)}`;

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-analytics] Heatmap fetch failed:', resp.status);
            return;
        }

        const data: HeatmapPayload = await resp.json();
        renderHeatmap(data.days);
    } catch (err) {
        console.error('[mc-analytics] Heatmap fetch error:', err);
    }
}

function renderHeatmap(days: Record<string, HeatmapDay>): void {
    const container = document.getElementById('mc-analytics-heatmap');
    if (!container) return;

    // Clear existing content
    container.innerHTML = '';

    // Calculate max hours for color scaling
    const hourValues = Object.values(days).map((d) => d.hours);
    const maxHours = hourValues.length > 0 ? Math.max(...hourValues) : 1;

    // Calculate start date: 52 weeks ago, aligned to Sunday
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const startDate = new Date(today);
    startDate.setDate(startDate.getDate() - 52 * 7);
    // Align to Sunday (day 0)
    const startDow = startDate.getDay(); // 0=Sun
    startDate.setDate(startDate.getDate() - startDow);

    const totalWeeks = 53;

    // Phase 181: isDark reader removed (Pitfall 6 — the prior code read from document.body which is
    // the wrong element; Phase 178 sets data-bs-theme on <html> not <body>). STATE.md single-reader
    // constraint restored: only getChartTheme() may read the theme. Empty/future heatmap cells use
    // var(--ksat-surface-raised) — resolves live at paint from [data-bs-theme] cascade, no JS branching.

    // Outer wrapper for overflow on mobile
    const wrapper = document.createElement('div');
    wrapper.style.overflowX = 'auto';

    // Build the grid layout: day-labels col + 53 week cols
    const grid = document.createElement('div');
    grid.className = 'mc-heatmap-grid';

    // Day labels column (Mon, Wed, Fri)
    const dayLabelsCol = document.createElement('div');
    dayLabelsCol.style.display = 'flex';
    dayLabelsCol.style.flexDirection = 'column';
    dayLabelsCol.style.gap = '2px';
    dayLabelsCol.style.paddingTop = '18px'; // leave room for month labels

    const dayNames = ['', 'Mon', '', 'Wed', '', 'Fri', ''];
    for (const name of dayNames) {
        const label = document.createElement('div');
        label.className = 'mc-heatmap-label';
        label.style.height = '12px';
        label.style.lineHeight = '12px';
        label.textContent = name;
        dayLabelsCol.appendChild(label);
    }
    grid.appendChild(dayLabelsCol);

    // Track month label positions
    let lastMonth = -1;

    for (let week = 0; week < totalWeeks; week++) {
        const weekCol = document.createElement('div');
        weekCol.style.display = 'flex';
        weekCol.style.flexDirection = 'column';
        weekCol.style.gap = '2px';

        // Month label
        const monthLabel = document.createElement('div');
        monthLabel.className = 'mc-heatmap-label';
        monthLabel.style.height = '16px';
        monthLabel.style.lineHeight = '16px';
        monthLabel.style.marginBottom = '2px';

        const weekStartDate = new Date(startDate);
        weekStartDate.setDate(weekStartDate.getDate() + week * 7);
        const month = weekStartDate.getMonth();
        if (month !== lastMonth) {
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
            monthLabel.textContent = monthNames[month];
            lastMonth = month;
        }
        weekCol.appendChild(monthLabel);

        // Day cells for this week
        for (let dow = 0; dow < 7; dow++) {
            const cellDate = new Date(startDate);
            cellDate.setDate(cellDate.getDate() + week * 7 + dow);

            const cell = document.createElement('div');
            cell.className = 'mc-heatmap-cell';

            const isFuture = cellDate > today;
            if (isFuture) {
                cell.classList.add('mc-heatmap-future');
                // var(--ksat-surface-raised) resolves live at paint — no JS theme branch needed
                cell.style.backgroundColor = 'var(--ksat-surface-raised)';
            } else {
                const dateStr = formatDate(cellDate);
                const dayData = days[dateStr];
                const hours = dayData ? dayData.hours : 0;
                cell.style.backgroundColor = intensityToGreen(hours, maxHours);

                const tooltipTitle = hours > 0
                    ? `${dateStr}: ${hours.toFixed(1)} hrs${dayData.players ? ' -- ' + dayData.players : ''}`
                    : `${dateStr}: No activity`;
                cell.dataset.bsToggle = 'tooltip';
                cell.dataset.bsTitle = tooltipTitle;
            }

            weekCol.appendChild(cell);
        }

        grid.appendChild(weekCol);
    }

    wrapper.appendChild(grid);
    container.appendChild(wrapper);

    // Color legend
    const legend = document.createElement('div');
    legend.className = 'mc-heatmap-legend';
    // Use var(--ksat-surface-raised) for the "empty" legend swatch — resolves live at paint
    legend.innerHTML = `
        <span class="mc-heatmap-label">Less</span>
        <div class="mc-heatmap-cell" style="background-color: var(--ksat-surface-raised)"></div>
        <div class="mc-heatmap-cell" style="background-color: #9be9a8"></div>
        <div class="mc-heatmap-cell" style="background-color: #40c463"></div>
        <div class="mc-heatmap-cell" style="background-color: #30a14e"></div>
        <div class="mc-heatmap-cell" style="background-color: #216e39"></div>
        <span class="mc-heatmap-label">More</span>
    `;
    container.appendChild(legend);

    // Initialize Bootstrap tooltips on heatmap cells
    init_tooltips();
}

// Phase 181: isDark parameter removed — 0-hours case now handled at call site with var(--ksat-surface-raised).
// Only non-zero intensity colors remain here (green palette = data identity, not chrome, D-01 — kept fixed).
function intensityToGreen(hours: number, maxHours: number): string {
    if (hours === 0) {
        // Caller should use 'var(--ksat-surface-raised)' for empty cells; this path is a fallback safety.
        return 'var(--ksat-surface-raised)';
    }

    const ratio = hours / Math.max(maxHours, 0.001);

    if (ratio < 0.25) return '#9be9a8';
    if (ratio < 0.50) return '#40c463';
    if (ratio < 0.75) return '#30a14e';
    return '#216e39';
}

// ---------------------------------------------------------------------------
// Data fetching (chart)
// ---------------------------------------------------------------------------

async function fetchChartData(from?: string, to?: string): Promise<void> {
    const loadingEl = document.getElementById('mc-chart-loading');
    const emptyEl = document.getElementById('mc-chart-empty');
    const chartEl = document.getElementById('mc-analytics-chart');

    if (loadingEl) loadingEl.style.display = 'block';
    if (emptyEl) emptyEl.classList.add('d-none');

    let url = `?do=getAnalyticsChart&server=${encodeURIComponent(currentServer)}&range=${encodeURIComponent(currentRange)}`;
    if (from && to) {
        url += `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }

    try {
        const resp = await fetch(url, {
            headers: { 'X-Requested-With': 'XMLHttpRequest' },
        });

        if (!resp.ok) {
            console.error('[mc-analytics] Chart fetch failed:', resp.status);
            if (loadingEl) loadingEl.style.display = 'none';
            return;
        }

        const data: ChartPayload = await resp.json();

        if (loadingEl) loadingEl.style.display = 'none';

        // Check for empty data
        const hasData = data.series.some((s) => s.points.length > 0);
        if (!hasData) {
            if (chartEl) chartEl.style.display = 'none';
            if (emptyEl) emptyEl.classList.remove('d-none');
            return;
        }

        if (chartEl) chartEl.style.display = '';
        if (emptyEl) emptyEl.classList.add('d-none');

        renderChart(data);
    } catch (err) {
        console.error('[mc-analytics] Chart fetch error:', err);
        if (loadingEl) loadingEl.style.display = 'none';
    }
}

// ---------------------------------------------------------------------------
// Chart rendering
// ---------------------------------------------------------------------------

function renderChart(data: ChartPayload): void {
    if (chartInstance) {
        chartInstance.destroy();
        chartInstance = null;
    }

    const series = data.series.map((s, i) => ({
        type: 'area' as const,
        name: s.name,
        data: s.points,
        color: SERVER_COLORS[i % SERVER_COLORS.length],
    }));

    // Phase 181: getChartTheme() supplies chrome (background, axis, tooltip surface).
    // backgroundColor: 'transparent' keeps the card token visible through the chart.
    // No data-bs-theme reader here — only getChartTheme() reads the theme (STATE.md constraint).
    // time.useUTC:false is set globally in src/index.ts — no per-chart override needed; omitted here.
    chartInstance = Highcharts.chart('mc-analytics-chart', Highcharts.merge(getChartTheme(), {
        chart: {
            type: 'area',
            backgroundColor: 'transparent',
        },
        title: {
            text: '',
        },
        plotOptions: {
            area: {
                stacking: data.stacked ? 'normal' : undefined,
                marker: { enabled: false },
                lineWidth: 1,
                fillOpacity: 0.3,
            },
        },
        xAxis: {
            type: 'datetime',
        },
        yAxis: {
            min: 0,
            allowDecimals: false,
            title: { text: 'Players' },
        },
        tooltip: {
            shared: true,
            xDateFormat: '%b %e %H:%M',
        },
        legend: {
            enabled: data.stacked,
        },
        series,
        credits: { enabled: false },
    }));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
}
